import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://heilao-buda.com/' },
          {
            '@type': 'ListItem',
            position: 2,
            name: currentCategory?.name || '',
            item: `https://heilao-buda.com/category/${slug}`,
          },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${currentCategory?.name || ''} - 黑料不打`,
        description: `浏览黑料不打平台上关于${currentCategory?.name || ''}行业的真实用户评价与品牌口碑信息`,
        url: `https://heilao-buda.com/category/${slug}`,
      },
    ],
  };

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
              <i className="ri-error-warning-line text-2xl"></i>
            </div>
            <p className="mt-4 text-foreground-500">分类不存在</p>
            <Link to="/" className="mt-4 inline-flex items-center text-sm text-primary-600 hover:text-primary-700 cursor-pointer">
              <i className="ri-arrow-left-line mr-1"></i> 返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <Navbar />

      <main className="pt-16">
        <div className="w-full px-4 md:px-6 py-6 md:py-8 bg-background-50 border-b border-background-200/60">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: currentCategory.name },
            ]}
          />
        </div>

        <section className="py-6 md:py-8 bg-background-50 border-b border-background-200/60">
          <div className="w-full px-4 md:px-6">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm text-foreground-500">
              共 {categoryArticles.length} 篇相关调查报道
            </p>
          </div>
        </section>

        <section className="py-4 md:py-6 bg-background-50 border-b border-background-200/60 overflow-x-auto">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-2 flex-nowrap min-w-max">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                    cat.slug === slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-xs`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            {categoryArticles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-inbox-line text-2xl"></i>
                </div>
                <p className="mt-4 text-foreground-500">该分类暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="bg-background-100 rounded-lg overflow-hidden cursor-pointer group hover:bg-primary-50/50 transition-colors duration-200"
                  >
                    <div className="relative h-44 md:h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="inline-flex items-center px-2 py-0.5 bg-accent-500 text-white text-xs font-medium rounded-full whitespace-nowrap">
                          {article.categoryName}
                        </span>
                      </div>
                      {article.verified && (
                        <div className="absolute top-3 right-3">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-600 text-white text-xs rounded-full whitespace-nowrap">
                            <i className="ri-shield-check-line text-[10px]"></i> 已核实
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
                        {article.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-2 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-background-200/70">
                        <span className="text-xs text-foreground-400">{article.date}</span>
                        <span className="text-xs text-foreground-400">{article.views.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}